import {
  createContext,
  FC,
  ReactElement,
  useContext,
  useEffect,
  useState,
} from "react";
import { CreateEntities, UserStatus } from "@fena/toolkit-types";
import { useGetUser } from "../modules/private/hooks/useGetUser";
import { UserApiType, UserWithSelectedMembership } from "../util/types";
import { useLocation } from "react-router-dom";
import { useCompanyStore } from "../stores/useCompanyStore";
import api from "../util/api";
import { canUseDomain } from "../modules/open/signin-module/hooks/useSignIn";

interface IUserContext {
  user: UserWithSelectedMembership;
  setUser: (data: Partial<UserWithSelectedMembership>) => void;
  clearUser: () => void;
  refreshUserData: () => void;
}

export const defaultValues: UserWithSelectedMembership = {
  _id: "",
  createdAt: new Date(),
  cognitoReferenceId: "",
  createdBy: "",
  createdByEntity: CreateEntities.SYSTEM,
  selectedCompanyMembership: null,
  companyMemberships: [
    {
      createdAt: new Date(),
      role: "",
      status: UserStatus.ACTIVE,
      isSuperuser: false,
      company: "",
      createdBy: "",
      createdByEntity: CreateEntities.SYSTEM,
      job: "",
    },
  ],
};

const UserContext = createContext<IUserContext>({
  user: defaultValues,
  setUser: () => null,
  clearUser: () => null,
  refreshUserData: () => null,
});

export const UserContextProvider: FC<{ children: ReactElement }> = ({
  children,
}) => {
  const { userData: user, refreshUserData } = useGetUser();
  const [userData, setUserData] = useState(defaultValues);
  const { company } = useCompanyStore();

  const location = useLocation();
  useEffect(() => {
    if (company?.managedBy) {
      const validateDomain = async () => {
        const parentCustomizationSettings =
          await api.getParentCustomizationSettings();
        if (
          parentCustomizationSettings?.partner_dashboard
            ?.partnerCustomizationEnabled
        ) {
          const customDomain =
            parentCustomizationSettings?.partner_dashboard?.domain;

          await canUseDomain(customDomain);
        }
      };
      validateDomain();
    }
  }, [location.pathname]);

  const handleUserUpdate = (data: Partial<UserApiType>) => {
    setUserData((prev) => ({ ...prev, ...data }));
  };

  const handleUserClear = () => {
    setUserData(defaultValues);
  };

  useEffect(() => {
    user &&
      setTimeout(() => {
        handleUserUpdate(user);
      }, 1500);
  }, [user]);

  return (
    <UserContext.Provider
      value={{
        user: userData,
        setUser: handleUserUpdate,
        clearUser: handleUserClear,
        refreshUserData,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUserContext = () => {
  return useContext(UserContext);
};
